import {
  Button,
  Checkbox,
  DialogV2,
  Divisor,
  Loader,
  OutreachEditor,
  OutreachMessagePlugin,
  Pill,
  Typography,
} from '@recruit-robin/robin-theme/web-components';
import { ChevronLeft, Eye, Star_06 } from '@recruit-robin/robin-theme/web-icons';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import {
  AILanguageSelector,
  useReachoutMessageGenerationLanguage,
  useReachoutMessageGenerationSetLanguage,
} from '@/features/reachout-message-generation';
import { useCalendarSchedulingLinkQuery } from '@/hooks/shared/useCalendarSchedulingLinkQuery';
import { useCampaignStore } from '@/stores/campaign/campaign.store';
import { SelectedVacancyPill } from '../../OutreachPanel/PanelBody/OutreachBodySection/SelectedVacancyPill';
import { ModalState } from '../RJCommunicationHub.types';
import { useStyles } from './GeneratedMessageDialog.styles';
import { GeneratedMessageDialogProps } from './GeneratedMessageDialog.types';
import { PreviewAsCandidateModal } from './PreviewAsCandidateModal/PreviewAsCandidateModal';
import { useIsIndeedFlow } from '@/shared/legacy/contexts/CommAgentFlowContext';

export const GeneratedMessageDialog = ({
  candidate,
  selectedVacancy,
  setSelectedVacancy,
  handleOutreachModalRequestClose,
  refs,
  handleConfirmMessage,
  dontShowMessagePreview = false,
  setDontShowMessagePreview,
  isLoading,
  navigateToStep,
  onGoBack,
  hideSelectedVacancyPill = false,
  generateMessage,
}: GeneratedMessageDialogProps) => {
  const { dictionary, language } = useContext(LocalizationContext);
  const classes = useStyles({});
  const [isPreviewAsCandidateModalOpen, setIsPreviewAsCandidateModalOpen] = useState(false);
  const campaignId = useCampaignStore((s) => s.campaign?.id);
  const { data: schedulerLink } = useCalendarSchedulingLinkQuery({
    esPersonId: candidate.es_person_id,
    campaignId,
    jobImprovedId: selectedVacancy?.db_id ?? undefined,
    autoFillJobContext: true,
  });

  const tokens = useMemo(() => {
    return {
      '{firstName}': candidate.firstName,
      '{lastName}': candidate.lastName,
      '{fullName}': candidate.name,
      '{location}': candidate.location?.name ?? '',
      '{lastPosition}': candidate.lastThreePositions[0]?.positionName ?? '',
      '{lastCompany}': candidate.lastThreePositions[0]?.companyName ?? '',
      '{recruiterFullName}': '',
      '{nameJobboard}': '',
      '{nameVacancy}': '',
      '{schedulerLink}': schedulerLink?.link
        ? `<a href="${schedulerLink.link}">${dictionary.schedulerLinkText}</a>`
        : '',
    };
  }, [candidate, schedulerLink, dictionary]);

  const enableMessagePlugin: Record<OutreachMessagePlugin, boolean> = useMemo(() => {
    return {
      Attachment: true,
      Link: true,
      List: true,
      PreventTextFormat: false,
      Tokens: true,
      SanitizeTextPlugin: true,
    };
  }, []);

  const isIndeedFlow = useIsIndeedFlow();

  const handleRemoveSelectedVacancy = () => {
    setSelectedVacancy(null);
    navigateToStep(ModalState.ACTIVATE_ROBIN_JR);
  };

  const setAiLanguage = useReachoutMessageGenerationSetLanguage();
  const aiLanguage = useReachoutMessageGenerationLanguage();
  const initialAiLanguageRef = useRef(aiLanguage);

  useEffect(() => {
    const initialAiLanguage = initialAiLanguageRef.current;

    if (initialAiLanguage !== aiLanguage) {
      generateMessage();
      initialAiLanguageRef.current = aiLanguage;
    }
  }, [aiLanguage]);

  return (
    <>
      <DialogV2.Root isVisible customCss={classes.dialog}>
        <DialogV2.Backdrop />
        <DialogV2.Close onRequestClose={handleOutreachModalRequestClose} />
        <DialogV2.Content customCss={classes.content}>
          <DialogV2.Header customCss={classes.title}>
            <Star_06 size={24} color={(c) => c.primary.fuchsia[40]} />
            <Typography variant={(v) => v.heading[3]} color={(c) => c.neutral.variant[80]}>
              {dictionary.reviewOutreachMessage}
            </Typography>
          </DialogV2.Header>
          <DialogV2.Body customCss={classes.body}>
            <div css={classes.editorContainer}>
              <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[70]}>
                {dictionary.reviewOutreachMessageDescription}
              </Typography>
              <div css={classes.previewAsCandidateContainer}>
                <Pill
                  label={
                    <Typography variant={(v) => v.supporting.label} color={(c) => c.neutral.variant[70]}>
                      {dictionary.previewAsCandidate}
                    </Typography>
                  }
                  startIcon={Eye}
                  onClick={() => setIsPreviewAsCandidateModalOpen(true)}
                  style="filter-tag"
                  type="subtle1"
                  size="small"
                  padding="both"
                />
              </div>
              <div css={classes.editorWrapper}>
                <OutreachEditor.Root
                  key={language}
                  dictionary={{
                    subjectPlaceholder: '',
                    messagePlaceholder: '',
                    attachFile: dictionary.attachFile,
                    link: {
                      displayText: dictionary.displayText,
                      insertLink: dictionary.insertLink,
                      newLink: dictionary.newLink,
                      removeLink: dictionary.removeLink,
                      URL: dictionary.URL,
                    },
                    personalizationTokens: dictionary.templateTokens,
                    signature: dictionary.signature,
                    tokenEmpty: dictionary.tokensEmpty,
                  }}
                  language={language}
                  values={tokens}>
                  <OutreachEditor.Subject
                    ref={refs.setSubject}
                    multiline={true}
                    endAdornment={<AILanguageSelector value={aiLanguage} onChange={setAiLanguage} />}
                  />
                  <Divisor color={(c) => c.neutral.variant[30]} py={0} />

                  <OutreachEditor.Message
                    customCss={classes.message}
                    ref={refs.setMessage}
                    enablePlugin={enableMessagePlugin}
                  />

                  {!hideSelectedVacancyPill && (
                    <div css={[selectedVacancy && classes.floatingWrapper]}>
                      {selectedVacancy && (
                        <SelectedVacancyPill selectedVacancy={selectedVacancy} onRemove={handleRemoveSelectedVacancy} />
                      )}
                    </div>
                  )}

                  <OutreachEditor.Toolbar customCss={classes.toolbar} variant="full" />
                </OutreachEditor.Root>
              </div>
              <Checkbox
                label={dictionary.dontShowMessagePreviewNextTime}
                checked={dontShowMessagePreview ?? false}
                onClick={(value) => setDontShowMessagePreview(value)}
              />
            </div>
          </DialogV2.Body>
          <DialogV2.Footer>
            <Button
              startIcon={<ChevronLeft size={16} color={(c) => c.neutral.variant[70]} />}
              label={dictionary.back}
              onClick={onGoBack}
              style="text"
              type="subtle2"
            />
            <Button
              label={isIndeedFlow ? dictionary.sendIndeedMessage : dictionary.confirmAndNext}
              onClick={handleConfirmMessage}
              style="filled"
              type="success"
              disabled={isLoading}
              startIcon={isLoading ? <Loader size={16} /> : undefined}
            />
          </DialogV2.Footer>
        </DialogV2.Content>
      </DialogV2.Root>

      {selectedVacancy && (
        <PreviewAsCandidateModal
          selectedVacancy={selectedVacancy}
          isVisible={isPreviewAsCandidateModalOpen}
          onClose={() => setIsPreviewAsCandidateModalOpen(false)}
          messageEditorRef={refs.message ?? null}
        />
      )}
    </>
  );
};
